import type { ComponentProps, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import {
  Activity, AlertTriangle, Archive, ArrowRight, Box, CalendarDays, Clock, Cloud, Database, FileCheck, Gauge,
  GitBranch, Globe, HardDrive, Info, KeyRound, Layers, Lightbulb, ListChecks, Lock, Monitor, Network, PenLine,
  Plug, Radio, Rocket, Scale, Server, Settings, Shield, UserRound, Users, Workflow, type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { COLORS, type ColorKey } from '@/help/diagrams/svg-primitives'
import { DiagramLegend } from '@/help/diagrams/DiagramLegend'
import { parseBlock, parseSetup, type BlockItem, type BlockLanguage } from './parse'
import { Guide } from './Guide'
import { SetupAssistant } from './SetupAssistant'

const ICONS: Record<string, LucideIcon> = {
  activity: Activity, archive: Archive, box: Box, calendar: CalendarDays, clock: Clock, cloud: Cloud,
  database: Database, 'file-check': FileCheck, gauge: Gauge, 'git-branch': GitBranch, globe: Globe,
  'hard-drive': HardDrive, key: KeyRound, layers: Layers, list: ListChecks, lock: Lock, monitor: Monitor,
  network: Network, plug: Plug, radio: Radio, rocket: Rocket, scale: Scale, server: Server, settings: Settings,
  shield: Shield, signature: PenLine, user: UserRound, users: Users, workflow: Workflow,
}

function toneColor(tone: string | undefined) {
  return tone && tone in COLORS ? COLORS[tone as ColorKey] : COLORS.neutral
}

/** Item bodies are inline markdown: no paragraph margins, internal links stay in the app. */
function InlineMd({ children }: { children: string }) {
  return (
    <ReactMarkdown
      components={{
        p: ({ children: c }: ComponentProps<'p'>) => <>{c} </>,
        a: ({ href, children: c }: ComponentProps<'a'>) =>
          href?.startsWith('/') ? <Link to={href}>{c}</Link> : <a href={href} target='_blank' rel='noopener noreferrer'>{c}</a>,
      }}
    >
      {children}
    </ReactMarkdown>
  )
}

function StatusBadge({ text }: { text: string }) {
  const tone = /^(active|enabled|live|yes)/i.test(text)
    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
    : /^(provisioned|planned|pending|disabled|beta)/i.test(text)
      ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
      : 'bg-muted text-muted-foreground'
  return (
    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide', tone)}>
      {text}
    </span>
  )
}

function IconChip({ item }: { item: BlockItem }) {
  const Icon = ICONS[item.attrs.icon ?? ''] ?? Layers
  const c = toneColor(item.attrs.tone)
  return (
    <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border'
      style={{ background: c.bg, borderColor: c.stroke, color: c.stroke }} aria-hidden='true'>
      <Icon className='h-4 w-4' />
    </span>
  )
}

function Card({ item }: { item: BlockItem }) {
  const { badge, meta, link } = item.attrs
  const body: ReactNode = (
    <>
      <div className='flex items-start gap-3'>
        <IconChip item={item} />
        <div className='min-w-0 flex-1'>
          <div className='flex items-start justify-between gap-2'>
            <div className='font-semibold leading-snug text-foreground'>{item.title}</div>
            {badge && <StatusBadge text={badge} />}
          </div>
          {meta && <div className='mt-0.5 truncate font-mono text-[11px] text-muted-foreground'>{meta}</div>}
        </div>
      </div>
      {item.body && (
        <div className='mt-2.5 text-[13px] leading-relaxed text-muted-foreground'>
          <InlineMd>{item.body}</InlineMd>
        </div>
      )}
      {link && (
        <span className='mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary'>
          Open <ArrowRight className='h-3 w-3 transition-transform group-hover:translate-x-0.5' />
        </span>
      )}
    </>
  )
  const cls = 'group block rounded-xl border bg-card p-4 shadow-sm'
  return link?.startsWith('/') ? (
    <Link to={link} className={cn(cls, 'help-block-link transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md')}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  )
}

function Cards({ items }: { items: BlockItem[] }) {
  return (
    <div className={cn('my-5 grid gap-3', items.length > 1 && 'sm:grid-cols-2')}>
      {items.map(item => <Card key={item.title} item={item} />)}
    </div>
  )
}

function Steps({ items }: { items: BlockItem[] }) {
  return (
    <ol className='help-steps my-5 !list-none !pl-0'>
      {items.map((item, i) => (
        <li key={item.title} className='relative flex gap-3 pb-4 last:pb-0'>
          {i < items.length - 1 && <span className='absolute left-[13px] top-7 h-[calc(100%-1.5rem)] w-px bg-border' aria-hidden='true' />}
          <span className='z-[1] flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-primary/30 bg-background text-xs font-semibold text-primary'>
            {i + 1}
          </span>
          <div className='min-w-0 flex-1 pt-0.5'>
            <div className='flex flex-wrap items-center gap-2 font-semibold leading-snug text-foreground'>
              {item.title}
              {item.attrs.meta && <code>{item.attrs.meta}</code>}
              {item.attrs.badge && <StatusBadge text={item.attrs.badge} />}
            </div>
            {item.body && (
              <div className='mt-0.5 text-[13px] leading-relaxed text-muted-foreground'>
                <InlineMd>{item.body}</InlineMd>
              </div>
            )}
          </div>
        </li>
      ))}
    </ol>
  )
}

const CALLOUT_TONES = {
  info: { Icon: Info, cls: 'border-blue-500/30 bg-blue-500/5 [&_.callout-icon]:text-blue-600 dark:[&_.callout-icon]:text-blue-400' },
  warning: { Icon: AlertTriangle, cls: 'border-amber-500/30 bg-amber-500/5 [&_.callout-icon]:text-amber-600 dark:[&_.callout-icon]:text-amber-400' },
  tip: { Icon: Lightbulb, cls: 'border-emerald-500/30 bg-emerald-500/5 [&_.callout-icon]:text-emerald-600 dark:[&_.callout-icon]:text-emerald-400' },
} as const

function Callouts({ items }: { items: BlockItem[] }) {
  return (
    <>
      {items.map(item => {
        const tone = CALLOUT_TONES[(item.attrs.tone ?? 'info') as keyof typeof CALLOUT_TONES] ?? CALLOUT_TONES.info
        return (
          <aside key={item.title} className={cn('my-5 flex gap-3 rounded-xl border p-4', tone.cls)}>
            <tone.Icon className='callout-icon mt-0.5 h-4 w-4 shrink-0' aria-hidden='true' />
            <div className='min-w-0 text-[13px] leading-relaxed'>
              <div className='font-semibold text-foreground'>{item.title}</div>
              {item.body && (
                <div className='mt-0.5 text-muted-foreground'>
                  <InlineMd>{item.body}</InlineMd>
                </div>
              )}
            </div>
          </aside>
        )
      })}
    </>
  )
}

/** Renders a ```cards / ```steps / ```callout / ```legend / ```guide fence (see `parse.ts`). */
export function StructuredBlock({ lang, source }: { lang: BlockLanguage; source: string }) {
  if (lang === 'legend') return <DiagramLegend />
  if (lang === 'guide') {
    const { intro, items } = parseBlock(source, true)
    return <Guide title={intro} items={items} />
  }
  if (lang === 'setup') {
    const { intro, topics } = parseSetup(source)
    return <SetupAssistant intro={intro} topics={topics} />
  }
  const { items } = parseBlock(source)
  if (lang === 'cards') return <Cards items={items} />
  if (lang === 'steps') return <Steps items={items} />
  return <Callouts items={items} />
}
